import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [connReqs, setConnReqs] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", contact_person: "", email: "", phone: "", city: "", gst_number: "", reason: "" });
  const navigate = useNavigate();

  const load = () => {
    api.get("/suppliers").then((r) => setSuppliers(r.data.suppliers)).catch(() => toast.error("Failed to load suppliers"));
    api.get("/connections/pending").then((r) => setConnReqs(r.data.requests)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const resolveConn = async (id, action) => {
    try {
      await api.post(`/connections/${id}/resolve`, { action });
      toast.success(action === "approve" ? "Connection approved — seller can now see this workspace" : "Connection request rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/suppliers", form);
      toast.success(r.data.message || (form.create_login ? "Supplier added with portal login" : "Supplier added"));
      setOpen(false);
      setForm({ name: "", contact_person: "", email: "", phone: "", city: "", gst_number: "", reason: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="suppliers-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Suppliers</h1>
          <p className="text-sm text-muted-foreground">{suppliers.length} suppliers in directory</p>
        </div>
        <Button data-testid="add-supplier-btn" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800"><Plus className="mr-1.5 h-4 w-4" /> Add Seller</Button>
      </div>

      {connReqs.length > 0 && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FEF3C7]/40" data-testid="connection-requests">
          <h2 className="border-b border-[#FDE68A] px-5 py-3 font-heading text-base font-semibold text-[#92400E]">Seller Connection Requests</h2>
          <div className="divide-y divide-[#FDE68A]/60">
            {connReqs.map((r) => (
              <div key={r.id} data-testid={`conn-req-${r.id.slice(0, 8)}`} className="flex flex-wrap items-center gap-4 px-5 py-3.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{r.supplier_name}</p>
                  <p className="text-xs text-muted-foreground">Requested by {r.requested_by}{r.message ? ` · "${r.message}"` : ""}</p>
                </div>
                <Button size="sm" data-testid={`conn-approve-${r.id.slice(0, 8)}`} onClick={() => resolveConn(r.id, "approve")} className="bg-[#166534] hover:bg-[#14532D]">Approve</Button>
                <Button size="sm" variant="outline" data-testid={`conn-reject-${r.id.slice(0, 8)}`} onClick={() => resolveConn(r.id, "reject")} className="border-red-200 text-red-700 hover:bg-red-50">Reject</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full text-sm" data-testid="suppliers-table">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-medium">Supplier</th>
              <th className="px-4 py-3 font-medium">Contact</th>
              <th className="px-4 py-3 font-medium">WhatsApp</th>
              <th className="px-4 py-3 font-medium text-right">Active</th>
              <th className="px-4 py-3 font-medium text-right">Completed</th>
              <th className="px-4 py-3 font-medium text-right">Delayed</th>
              <th className="px-4 py-3 font-medium text-right">On-Time %</th>
              <th className="px-4 py-3 font-medium text-right">Avg Response</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((s) => (
              <tr key={s.id} data-testid={`supplier-row-${s.name.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => navigate(`/buyer/suppliers/${s.id}`)} className="cursor-pointer border-b transition-colors last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <p className="font-medium">{s.name}</p>
                  <p className="text-xs text-muted-foreground">{s.city}</p>
                </td>
                <td className="px-4 py-3">
                  <p>{s.contact_person}</p>
                  <p className="text-xs text-muted-foreground">{s.email}</p>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{s.phone}</td>
                <td className="px-4 py-3 text-right font-mono">{s.metrics.active_pos}</td>
                <td className="px-4 py-3 text-right font-mono">{s.metrics.completed_pos}</td>
                <td className={`px-4 py-3 text-right font-mono ${s.metrics.delayed_pos > 0 ? "font-semibold text-[#DC2626]" : ""}`}>{s.metrics.delayed_pos}</td>
                <td className="px-4 py-3 text-right font-mono">{s.metrics.on_time_pct != null ? `${s.metrics.on_time_pct}%` : "—"}</td>
                <td className="px-4 py-3 text-right font-mono">{s.metrics.avg_response_hours != null ? `${s.metrics.avg_response_hours}h` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">Add Seller — Enterprise Approval Required</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">New sellers join your approved supplier network only after your Enterprise Admin approves the request.</p>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Company / Supplier Name *</Label><Input data-testid="supplier-form-name" required className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Contact Person *</Label><Input data-testid="supplier-form-contact" required className="mt-1.5" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
              <div><Label>City</Label><Input data-testid="supplier-form-city" className="mt-1.5" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>Email *</Label><Input data-testid="supplier-form-email" type="email" required className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>WhatsApp Number *</Label><Input data-testid="supplier-form-phone" required className="mt-1.5" placeholder="+91 98XXX XXXXX" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="col-span-2"><Label>GST Number *</Label><Input data-testid="supplier-form-gst" required className="mt-1.5 font-mono" placeholder="27ABCDE1234F1Z5" maxLength={15} value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })} /></div>
            </div>
            <div><Label>Reason / Comment</Label><Input data-testid="supplier-form-reason" className="mt-1.5" placeholder="Why do you need this seller?" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="supplier-form-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit for Approval
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
